import React, { useState, useEffect, useMemo, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Drawer,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  LocalOffer as TagIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Add as AddIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationActionHeaderTitleSx,
  getConfigurationAddButtonSx,
  getConfigurationCardGridSpacing,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../utils/drawerOverlayProps';
import { getFiles, uploadFile, deleteFile, updateFileTags, getConfigurationRoomTypes, getPredefinedTags } from '../services/api';
import { formatHotelDate } from '../utils/dateUtils';


// Theme color constant
const THEME_COLOR = '#4ce1b6';

const FileManagerPage = () => {
  const theme = useTheme();
  const isMobileLayout = isConfigurationMobileLayout();
  const [isDark, setIsDark] = useState(() => detectThemeSync() === 'dark');
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff'),
    [isDark]
  );
  
  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setIsDark(newTheme === 'dark');
    });
    return cleanup;
  }, []);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('photos');
  const [fileType, setFileType] = useState('property');
  const [selectedFile, setSelectedFile] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedRoomTypeTag, setSelectedRoomTypeTag] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFile, setMenuFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Document drawer states
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    document_type: '',
    title: '',
    description: '',
    expiry_date: null,
    document: null,
    is_active: true,
    sort_order: 0,
  });
  const [documentFormErrors, setDocumentFormErrors] = useState({});
  
  // Photo upload drawer states
  const [photoDrawerOpen, setPhotoDrawerOpen] = useState(false);
  const [photoFormData, setPhotoFormData] = useState({
    room_type_id: '',
    description: '',
    image_type: 'other',
    is_primary: false,
    alt_text: '',
    sort_order: 0,
    image: null,
    tags: [],
  });
  const [photoFormErrors, setPhotoFormErrors] = useState({});
  const [selectedPhotoTag, setSelectedPhotoTag] = useState(null);
  const [selectedPhotoRoomType, setSelectedPhotoRoomType] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, [category, fileType]);

  useEffect(() => {
    fetchPredefinedTags();
    if (fileType === 'room_type') {
      fetchRoomTypes();
    }
  }, [fileType]);

  const fetchPredefinedTags = async () => {
    try {
      const data = await getPredefinedTags();
      setPredefinedTags(data.tags || []);
    } catch (error) {}
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await getConfigurationRoomTypes();
      setRoomTypes(data || []);
    } catch (error) {}
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const data = await getFiles(category, fileType);
      setFiles(data.files || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to fetch files',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate PDF for documents
    if (category === 'documents' && !file.name.toLowerCase().endsWith('.pdf')) {
      setSnackbar({
        open: true,
        message: 'Only PDF files are allowed for documents',
        severity: 'error',
      });
      return;
    }

    setUploading(true);
    try {
      await uploadFile(file, category, fileType);
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success',
      });
      fetchFiles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to upload file',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenDocumentDrawer = (documentType) => {
    setDocumentFormData({
      document_type: documentType,
      title: '',
      description: '',
      expiry_date: null,
      document: null,
      is_active: true,
      sort_order: 0,
    });
    setDocumentFormErrors({});
    setDocumentDrawerOpen(true);
  };

  const handleDocumentFormChange = (field, value) => {
    setDocumentFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (documentFormErrors[field]) {
      setDocumentFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDocumentFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setDocumentFormErrors(prev => ({
          ...prev,
          document: 'Only PDF files are allowed',
        }));
        return;
      }
      handleDocumentFormChange('document', file);
      // Auto-fill title if empty
      if (!documentFormData.title) {
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        handleDocumentFormChange('title', fileNameWithoutExt);
      }
    }
  };

  const validateDocumentForm = () => {
    const errors = {};
    
    if (!documentFormData.document_type) {
      errors.document_type = 'Document type is required';
    }
    
    if (!documentFormData.document) {
      errors.document = 'Please select a PDF file';
    }
    
    setDocumentFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDocumentSubmit = async () => {
    if (!validateDocumentForm()) {
      return;
    }

    setUploading(true);
    try {
      await uploadFile(documentFormData.document, 'documents', null, {
        document_type: documentFormData.document_type,
        title: documentFormData.title || undefined,
        description: documentFormData.description || undefined,
        expiry_date: documentFormData.expiry_date ? moment(documentFormData.expiry_date).format('YYYY-MM-DD') : undefined,
      });

      setSnackbar({
        open: true,
        message: 'Document uploaded successfully',
        severity: 'success',
      });
      setDocumentDrawerOpen(false);
      setDocumentFormData({
        document_type: 'other',
        title: '',
        description: '',
        expiry_date: null,
        document: null,
        is_active: true,
        sort_order: 0,
      });
      setDocumentFormErrors({});
      fetchFiles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to upload document',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (fileId) => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    try {
      await deleteFile(fileToDelete.id, category, category === 'documents' ? null : fileType);
      setSnackbar({
        open: true,
        message: 'File deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      fetchFiles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to delete file',
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  };

  const handlePhotoFormChange = (field, value) => {
    setPhotoFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (photoFormErrors[field]) {
      setPhotoFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePhotoFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate image file type
      if (!file.type.startsWith('image/')) {
        setPhotoFormErrors(prev => ({
          ...prev,
          image: 'Only image files are allowed',
        }));
        return;
      }
      handlePhotoFormChange('image', file);
    }
  };

  const validatePhotoForm = () => {
    const errors = {};
    
    if (!photoFormData.image) {
      errors.image = 'Please select an image file';
    }
    
    setPhotoFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPhotoTag = (tagValue) => {
    if (tagValue && !photoFormData.tags.includes(tagValue)) {
      handlePhotoFormChange('tags', [...photoFormData.tags, tagValue]);
      setSelectedPhotoTag(null);
    }
  };

  const handleAddPhotoRoomType = () => {
    if (selectedPhotoRoomType && !photoFormData.tags.includes(selectedPhotoRoomType)) {
      handlePhotoFormChange('tags', [...photoFormData.tags, selectedPhotoRoomType]);
      // Also set room_type_id if it's a room type name
      const roomType = roomTypes.find(rt => rt.name === selectedPhotoRoomType);
      if (roomType) {
        handlePhotoFormChange('room_type_id', roomType.id);
      }
      setSelectedPhotoRoomType(null);
    }
  };

  const handleRemovePhotoTag = (tagToRemove) => {
    handlePhotoFormChange('tags', photoFormData.tags.filter(tag => tag !== tagToRemove));
    // Clear room_type_id if the removed tag was a room type
    const roomType = roomTypes.find(rt => rt.name === tagToRemove);
    if (roomType && photoFormData.room_type_id === roomType.id) {
      handlePhotoFormChange('room_type_id', '');
    }
  };

  const handlePhotoSubmit = async () => {
    if (!validatePhotoForm()) {
      return;
    }

    setUploading(true);
    try {
      await uploadFile(photoFormData.image, 'photos', fileType, {
        description: photoFormData.description || undefined,
        image_type: photoFormData.image_type,
        is_primary: photoFormData.is_primary,
        alt_text: photoFormData.alt_text || undefined,
        sort_order: photoFormData.sort_order || 0,
        tags: photoFormData.tags,
        room_type_id: fileType === 'room_type' && photoFormData.room_type_id ? photoFormData.room_type_id : undefined,
      });

      setSnackbar({
        open: true,
        message: 'Photo uploaded successfully',
        severity: 'success',
      });
      setPhotoDrawerOpen(false);
      setPhotoFormData({
        room_type_id: '',
        description: '',
        image_type: 'other',
        is_primary: false,
        alt_text: '',
        sort_order: 0,
        image: null,
        tags: [],
      });
      setPhotoFormErrors({});
      setSelectedPhotoTag(null);
      setSelectedPhotoRoomType(null);
      fetchFiles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to upload photo',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleTagClick = (file) => {
    setSelectedFile(file);
    setTags(file.tags || []);
    setDrawerOpen(true);
  };

  const handleAddTag = (tagValue) => {
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue]);
      setSelectedTag(null);
    }
  };

  const handleAddRoomTypeTag = () => {
    if (selectedRoomTypeTag && !tags.includes(selectedRoomTypeTag)) {
      setTags([...tags, selectedRoomTypeTag]);
      setSelectedRoomTypeTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveTags = async () => {
    if (!selectedFile) return;

    try {
      await updateFileTags(selectedFile.id, tags, category, fileType);
      setSnackbar({
        open: true,
        message: 'Tags updated successfully',
        severity: 'success',
      });
      setDrawerOpen(false);
      fetchFiles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.error || 'Failed to update tags',
        severity: 'error',
      });
    }
  };

  const handleMenuOpen = (event, file) => {
    setMenuAnchor(event.currentTarget);
    setMenuFile(file);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuFile(null);
  };

  const categories = [
    {
      id: 'photos',
      label: 'Photos',
      icon: <ImageIcon />,
      subcategories: [
        { id: 'property', label: 'Property' },
        { id: 'room_type', label: 'Room type' },
      ],
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: <DescriptionIcon />,
      subcategories: [],
    },
  ];

  const currentCategory = categories.find(c => c.id === category);
  const currentSubcategory = currentCategory?.subcategories.find(s => s.id === fileType);

  return (
    <Box sx={getConfigurationEmbeddedRootSx(isMobileLayout)}>
      {/* Header */}
      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Typography
          variant="h5"
          sx={{
            ...getConfigurationActionHeaderTitleSx(isMobileLayout),
            fontWeight: 600,
            color: isDark ? '#f8fafc' : '#212121',
          }}
        >
          File Manager
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={category === 'documents' ? 'application/pdf' : 'image/*'}
            style={{ display: 'none' }}
            onChange={handleUpload}
            disabled={uploading}
          />
          {category === 'documents' ? (
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              onClick={() => handleOpenDocumentDrawer('other')}
              disabled={uploading}
              sx={getConfigurationAddButtonSx(THEME_COLOR, isMobileLayout)}
            >
              {uploading ? 'Uploading...' : '+ Upload Document'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              onClick={() => {
                setPhotoFormData({
                  room_type_id: '',
                  description: '',
                  image_type: 'other',
                  is_primary: false,
                  alt_text: '',
                  sort_order: 0,
                  image: null,
                  tags: [],
                });
                setPhotoFormErrors({});
                setSelectedPhotoTag(null);
                setSelectedPhotoRoomType(null);
                setPhotoDrawerOpen(true);
              }}
              disabled={uploading}
              sx={getConfigurationAddButtonSx(THEME_COLOR, isMobileLayout)}
            >
              {uploading ? 'Uploading...' : '+ Upload'}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={getConfigurationCardGridSpacing(isMobileLayout)}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: isDark ? alpha('#ffffff', 0.08) : 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
              Categories
            </Typography>
            {categories.map((cat) => (
              <Box key={cat.id} sx={{ mb: 1 }}>
                <Box
                  onClick={() => {
                    setCategory(cat.id);
                    if (cat.subcategories.length > 0) {
                      setFileType(cat.subcategories[0].id);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: category === cat.id ? 'action.selected' : 'transparent',
                    color: category === cat.id ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {category === cat.id ? <FolderOpenIcon /> : <FolderIcon />}
                  <Typography variant="body2" sx={{ fontWeight: category === cat.id ? 600 : 400 }}>
                    {cat.label}
                  </Typography>
                </Box>
                {category === cat.id &&
                  cat.subcategories.map((subcat) => (
                    <Box
                      key={subcat.id}
                      onClick={() => setFileType(subcat.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        pl: 4,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: fileType === subcat.id ? 'action.selected' : 'transparent',
                        color: fileType === subcat.id ? 'primary.main' : 'text.primary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {fileType === subcat.id ? <FolderOpenIcon /> : <FolderIcon />}
                      <Typography variant="body2" sx={{ fontWeight: fileType === subcat.id ? 600 : 400 }}>
                        {subcat.label}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{ p: 3, minHeight: '500px', border: 1, borderColor: isDark ? alpha('#ffffff', 0.08) : 'divider' }}>
            {/* Filter Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentSubcategory?.label || currentCategory?.label || 'Files'}
              </Typography>
              <IconButton>
                <FilterListIcon />
              </IconButton>
            </Box>

            {/* Files Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : category === 'documents' ? (
              // Dynamic Document Cards - Material Design
              files.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', textAlign: 'center' }}>
                <UploadIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No Documents Uploaded
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click the upload button above to add your first document
                </Typography>
              </Box>
              ) : (
                <Grid container spacing={2}>
                  {files.map((doc) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column', 
                          overflow: 'hidden',
                          border: isDark ? `1px solid ${alpha('#ffffff', 0.08)}` : 'none',
                        }}
                      >
                        {/* PDF Preview */}
                        <Box sx={{ flex: 1, minHeight: '200px', maxHeight: '200px', overflow: 'hidden', bgcolor: 'grey.100', position: 'relative' }}>
                          <iframe
                            src={`${doc.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={doc.name}
                          />
                        </Box>
                        {/* Document Info */}
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 0.5, fontWeight: 600, textTransform: 'capitalize' }}>
                            {doc.document_type_display || doc.document_type}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {doc.title || doc.name}
                          </Typography>
                          {doc.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {doc.description}
                            </Typography>
                          )}
                          {doc.expiry_date && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1.5, fontWeight: 500 }}>
                              Expires: {formatHotelDate(doc.expiry_date)}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(doc.url, '_blank')}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}
                            >
                              Open
                            </Button>
                            <IconButton size="small" onClick={() => handleDeleteClick(doc.id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )
            ) : files.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No files found. Upload your first file to get started.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {files.map((file) => (
                  <Grid item xs={6} sm={4} md={3} key={file.id}>
                    <Card sx={{ 
                      height: '100%', 
                      position: 'relative',
                      border: isDark ? `1px solid ${alpha('#ffffff', 0.08)}` : 'none',
                      '&:hover': {
                        borderColor: isDark ? '#4ce1b6' : 'transparent',
                      },
                    }}>
                      <CardActionArea>
                        <CardMedia
                          component="img"
                          height="200"
                          image={file.thumbnail_url || file.url}
                          alt={file.title || file.name}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {file.name}
                          </Typography>
                          {file.tags && file.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                              {file.tags.slice(0, 2).map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTagClick(file);
                                  }}
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              ))}
                              {file.tags.length > 2 && (
                                <Chip
                                  label={`+${file.tags.length - 2}`}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTagClick(file);
                                  }}
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              )}
                            </Box>
                          )}
                          {(!file.tags || file.tags.length === 0) && (
                            <Button
                              size="small"
                              startIcon={<TagIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTagClick(file);
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', p: 0.5 }}
                            >
                              Add Tag
                            </Button>
                          )}
                        </CardContent>
                      </CardActionArea>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, file)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Document Upload Drawer */}
      <Drawer
        anchor="right"
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Upload Document
            </Typography>
            <IconButton onClick={() => setDocumentDrawerOpen(false)} size="small" sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
            <Grid container spacing={2}>
              {/* Document Type */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!documentFormErrors.document_type}>
                  <InputLabel>Document Type</InputLabel>
                    <Select
                      value={documentFormData.document_type}
                      onChange={(e) => handleDocumentFormChange('document_type', e.target.value)}
                      label="Document Type"
                      MenuProps={drawerOverlays.selectMenuProps}
                    >
                    <MenuItem value="pan_card">PAN Card</MenuItem>
                    <MenuItem value="cin_certificate">CIN Certificate</MenuItem>
                    <MenuItem value="gst_certificate">GST Certificate</MenuItem>
                    <MenuItem value="cancelled_cheque">Cancelled Cheque</MenuItem>
                    <MenuItem value="aadhaar_card">AADHAAR Card</MenuItem>
                    <MenuItem value="passport">Passport</MenuItem>
                    <MenuItem value="driving_license">Driving License</MenuItem>
                    <MenuItem value="voter_id">Voter ID</MenuItem>
                    <MenuItem value="bank_statement">Bank Statement</MenuItem>
                    <MenuItem value="incorporation_certificate">Incorporation Certificate</MenuItem>
                    <MenuItem value="trade_license">Trade License</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {documentFormErrors.document_type && (
                    <FormHelperText>{documentFormErrors.document_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={documentFormData.title}
                  onChange={(e) => handleDocumentFormChange('title', e.target.value)}
                  error={!!documentFormErrors.title}
                  helperText={documentFormErrors.title}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={documentFormData.description}
                  onChange={(e) => handleDocumentFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  error={!!documentFormErrors.description}
                  helperText={documentFormErrors.description}
                />
              </Grid>

              {/* Expiry Date */}
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Expiry Date (Optional)"
                    value={documentFormData.expiry_date}
                    onChange={(newValue) => handleDocumentFormChange('expiry_date', newValue)}
                    slotProps={{
                      ...drawerOverlays.datePickerSlotProps,
                      textField: {
                        fullWidth: true,
                        error: !!documentFormErrors.expiry_date,
                        helperText: documentFormErrors.expiry_date,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Document Upload */}
              <Grid item xs={12}>
                <Box>
                  <input
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    id="document-file-input"
                    onChange={handleDocumentFileSelect}
                  />
                  <label htmlFor="document-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      sx={{ textTransform: 'none', py: 1.5 }}
                    >
                      {documentFormData.document ? documentFormData.document.name : 'Choose PDF File'}
                    </Button>
                  </label>
                  {documentFormErrors.document && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {documentFormErrors.document}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>

              {/* Sort Order */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sort Order"
                  type="number"
                  value={documentFormData.sort_order}
                  onChange={(e) => handleDocumentFormChange('sort_order', parseInt(e.target.value) || 0)}
                  error={!!documentFormErrors.sort_order}
                  helperText={documentFormErrors.sort_order}
                />
              </Grid>

              {/* Is Active */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={documentFormData.is_active}
                      onChange={(e) => handleDocumentFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDocumentDrawerOpen(false)}
              disabled={uploading}
              fullWidth
              sx={{
                borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                color: isDark ? '#ddd' : '#666',
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: '#4ce1b6',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDocumentSubmit}
              disabled={uploading || !documentFormData.document}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              fullWidth
              sx={{
                backgroundColor: '#4ce1b6',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4ce1b6',
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Photo Upload Drawer */}
      <Drawer
        anchor="right"
        open={photoDrawerOpen}
        onClose={() => setPhotoDrawerOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Upload Photo
            </Typography>
            <IconButton onClick={() => setPhotoDrawerOpen(false)} size="small" sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
            <Grid container spacing={2}>
              {/* Image Upload */}
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload-input"
                  onChange={handlePhotoFileSelect}
                />
                <label htmlFor="photo-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<UploadIcon />}
                    sx={{ textTransform: 'none', py: 1.5 }}
                  >
                    {photoFormData.image ? photoFormData.image.name : 'Choose Image File'}
                  </Button>
                </label>
                {photoFormErrors.image && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {photoFormErrors.image}
                  </FormHelperText>
                )}
                {photoFormData.image && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={URL.createObjectURL(photoFormData.image)}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Room Type Selection (only for room_type uploads) */}
              {fileType === 'room_type' && (
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!photoFormErrors.room_type_id}>
                    <InputLabel>Select Room Type</InputLabel>
                    <Select
                      value={selectedPhotoRoomType || ''}
                      onChange={(e) => {
                        const selectedRoomTypeName = e.target.value;
                        setSelectedPhotoRoomType(selectedRoomTypeName);
                        // Automatically add room type as tag when selected
                        if (selectedRoomTypeName) {
                          // Remove any existing room type tags and add the new one
                          const roomTypeNames = roomTypes.map(rt => rt.name);
                          const filteredTags = photoFormData.tags.filter(tag => !roomTypeNames.includes(tag));
                          const newTags = [...filteredTags, selectedRoomTypeName];
                          handlePhotoFormChange('tags', newTags);
                          // Set room_type_id
                          const roomType = roomTypes.find(rt => rt.name === selectedRoomTypeName);
                          if (roomType) {
                            handlePhotoFormChange('room_type_id', roomType.id);
                          }
                        }
                      }}
                      label="Select Room Type"
                      MenuProps={drawerOverlays.selectMenuProps}
                    >
                      {roomTypes
                        .filter((rt) => {
                          // Show room type if it's not in tags, or if it's the currently selected one
                          return !photoFormData.tags.includes(rt.name) || rt.name === selectedPhotoRoomType;
                        })
                        .map((rt) => (
                          <MenuItem key={rt.id} value={rt.name}>
                            {rt.name}
                          </MenuItem>
                        ))}
                    </Select>
                    {photoFormErrors.room_type_id && (
                      <FormHelperText>{photoFormErrors.room_type_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Tags */}
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Tags
                </Typography>
                <Autocomplete
                  freeSolo={false}
                  options={(() => {
                    if (fileType === 'property') {
                      // Property images: only predefined tags (exclude room type names)
                      const roomTypeNames = roomTypes.map(rt => rt.name);
                      return predefinedTags.filter(tag => !roomTypeNames.includes(tag) && !photoFormData.tags.includes(tag));
                    } else {
                      // Room type images: predefined tags + room type names
                      const roomTypeNames = roomTypes.map(rt => rt.name);
                      const allAvailableTags = [...predefinedTags, ...roomTypeNames];
                      return allAvailableTags.filter(tag => !photoFormData.tags.includes(tag));
                    }
                  })()}
                  value={selectedPhotoTag}
                  onChange={(event, newValue) => {
                    setSelectedPhotoTag(newValue);
                    if (newValue) {
                      handleAddPhotoTag(newValue);
                    }
                  }}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Tag"
                      placeholder="Select or type a tag"
                    />
                  )}
                />
                {photoFormData.tags.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {photoFormData.tags.map((tag, index) => {
                      const isRoomType = roomTypes.some((rt) => rt.name === tag);
                      return (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleRemovePhotoTag(tag)}
                          color={isRoomType ? 'primary' : 'default'}
                          sx={{ fontWeight: isRoomType ? 600 : 400 }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={photoFormData.description}
                  onChange={(e) => handlePhotoFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  error={!!photoFormErrors.description}
                  helperText={photoFormErrors.description}
                />
              </Grid>

              {/* Image Type */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!photoFormErrors.image_type}>
                  <InputLabel>Image Type</InputLabel>
                  <Select
                    value={photoFormData.image_type}
                    onChange={(e) => {
                      const selectedImageType = e.target.value;
                      handlePhotoFormChange('image_type', selectedImageType);
                      // If "Room" is selected, automatically add it as a tag
                      if (selectedImageType === 'room') {
                        if (!photoFormData.tags.includes('Room')) {
                          handlePhotoFormChange('tags', [...photoFormData.tags, 'Room']);
                        }
                      } else {
                        // Remove "Room" tag if another image type is selected
                        if (photoFormData.tags.includes('Room')) {
                          handlePhotoFormChange('tags', photoFormData.tags.filter(tag => tag !== 'Room'));
                        }
                      }
                    }}
                    label="Image Type"
                    MenuProps={drawerOverlays.selectMenuProps}
                  >
                    <MenuItem value="exterior">Exterior</MenuItem>
                    <MenuItem value="lobby">Lobby</MenuItem>
                    <MenuItem value="reception">Reception</MenuItem>
                    <MenuItem value="restaurant">Restaurant</MenuItem>
                    <MenuItem value="pool">Pool</MenuItem>
                    <MenuItem value="gym">Gym</MenuItem>
                    <MenuItem value="spa">Spa</MenuItem>
                    <MenuItem value="event">Event Space</MenuItem>
                    <MenuItem value="room">Room</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {photoFormErrors.image_type && (
                    <FormHelperText>{photoFormErrors.image_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Alt Text */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Alt Text (Accessibility)"
                  value={photoFormData.alt_text}
                  onChange={(e) => handlePhotoFormChange('alt_text', e.target.value)}
                  error={!!photoFormErrors.alt_text}
                  helperText={photoFormErrors.alt_text || "Alternative text for screen readers"}
                />
              </Grid>

              {/* Sort Order */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sort Order"
                  type="number"
                  value={photoFormData.sort_order}
                  onChange={(e) => handlePhotoFormChange('sort_order', parseInt(e.target.value) || 0)}
                  error={!!photoFormErrors.sort_order}
                  helperText={photoFormErrors.sort_order}
                />
              </Grid>

              {/* Is Primary */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={photoFormData.is_primary}
                      onChange={(e) => handlePhotoFormChange('is_primary', e.target.checked)}
                    />
                  }
                  label="Set as Primary/Featured Image"
                />
              </Grid>
            </Grid>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setPhotoDrawerOpen(false)}
              sx={{
                borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                color: isDark ? '#ddd' : '#666',
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: '#4ce1b6',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handlePhotoSubmit}
              disabled={uploading || !photoFormData.image}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              sx={{
                backgroundColor: '#4ce1b6',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4ce1b6',
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Tags Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Manage Tags
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >

          {selectedFile && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  File: {selectedFile.name}
                </Typography>
                {selectedFile.thumbnail_url && (
                  <Box
                    component="img"
                    src={selectedFile.thumbnail_url}
                    alt={selectedFile.name}
                    sx={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  />
                )}
              </Box>

              {/* Room Type Dropdown - Only for room type images */}
              {fileType === 'room_type' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Select Room Type
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={roomTypes
                        .filter((rt) => !tags.includes(rt.name)) // Only show room types not already in tags
                        .map((rt) => rt.name)}
                      value={selectedRoomTypeTag}
                      onChange={(event, newValue) => {
                        setSelectedRoomTypeTag(newValue || '');
                      }}
                      slotProps={drawerOverlays.autocompleteSlotProps}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select a room type"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option}>
                          {option}
                        </Box>
                      )}
                      noOptionsText="No room types available"
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddRoomTypeTag}
                      disabled={!selectedRoomTypeTag}
                      sx={{ minWidth: '100px' }}
                    >
                      Add
                    </Button>
                  </Box>
                  {selectedFile.room_type_name && (
                    <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                      Current Room Type: {selectedFile.room_type_name}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Tags {fileType === 'room_type' && '(Room Type + Additional Tags)'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {tags.map((tag, index) => {
                    const isRoomType = roomTypes.some((rt) => rt.name === tag);
                    return (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        color={isRoomType ? 'primary' : 'default'}
                        sx={{ fontWeight: isRoomType ? 600 : 400 }}
                      />
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={(() => {
                      // For property images: exclude room type names from predefined tags
                      // For room type images: include all predefined tags + room type names
                      if (fileType === 'property') {
                        const roomTypeNames = roomTypes.map(rt => rt.name);
                        return predefinedTags.filter(tag => !roomTypeNames.includes(tag) && !tags.includes(tag));
                      } else {
                        // Room type images: predefined tags + room type names
                        const roomTypeNames = roomTypes.map(rt => rt.name);
                        const allAvailableTags = [...predefinedTags, ...roomTypeNames];
                        return allAvailableTags.filter(tag => !tags.includes(tag));
                      }
                    })()}
                    value={selectedTag}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleAddTag(newValue);
                      }
                    }}
                    slotProps={drawerOverlays.autocompleteSlotProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={fileType === 'room_type' ? "Search and select additional tags (optional)" : "Search and select a tag"}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option}>
                        {option}
                      </Box>
                    )}
                    noOptionsText="No tags available"
                  />
                </Box>
                {fileType === 'room_type' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select room type from dropdown above, or add additional descriptive tags (e.g., "bedroom", "bathroom")
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Fixed Footer */}
        {selectedFile && (
          <Box
            sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                  color: isDark ? '#ddd' : '#666',
                  '&:hover': {
                    borderColor: '#4ce1b6',
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: '#4ce1b6',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveTags}
                sx={{
                  backgroundColor: '#4ce1b6',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#4ce1b6',
                    opacity: 0.9,
                    boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuFile) {
              handleTagClick(menuFile);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <TagIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Manage Tags</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuFile) {
              handleDeleteClick(menuFile.id);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} PaperProps={{ sx: { minWidth: '400px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{fileToDelete?.name || 'this file'}"?
            {fileToDelete?.file_type === 'document' && (
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'error.main', fontWeight: 500 }}>
                This action cannot be undone.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileManagerPage;

